import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Raw client for the refresh call itself — bypasses the global interceptor
// so a failing refresh can never recurse. Created on first use, so importing
// this module has no side effects: pages reach it through lib/stepUp.js (#854)
// and their tests mock axios without `create`.
let rawClient = null;
function getRawClient() {
    if (!rawClient) rawClient = axios.create();
    return rawClient;
}

export const SESSION_EXPIRED_EVENT = 'auth:session-expired';
export const TOKEN_REFRESHED_EVENT = 'auth:token-refreshed';

// Auth endpoints manage their own 401 handling (login form errors,
// checkAuth retry logic, the refresh call itself).
//
// `/auth/mfa/` covers /setup, /enable, /verify, /disable and /recovery/regenerate:
// every one of them returns 401 to mean "that code is wrong", not "your session
// expired". Retrying re-sends the wrong code, and on /verify the replay is charged
// against the 5-attempt per-challenge budget — five tries became two (#637).
//
// The trade-off, stated so it is not rediscovered: SKIP_PATHS is all-or-nothing
// per path, so an MFA call made on a genuinely expired ACCESS token now surfaces
// the endpoint's 401 text instead of refreshing silently. That is the right
// trade — the double-charge corrupts a budget whose predecessor permanently
// locked users out of the product (#594), while the expired-token case costs one
// retry to a user who is already typing a code.
const SKIP_PATHS = ['/auth/login', '/auth/refresh', '/auth/logout', '/auth/me', '/auth/sso', '/auth/mfa/'];

/**
 * Does this request URL belong to an endpoint that owns its own 401?
 *
 * The list carries TWO deliberate shapes and the matcher honours both:
 *
 *   - A bare endpoint (`/auth/me`) matches that endpoint and anything BELOW it
 *     (`/auth/sso` must keep matching `/auth/sso/authorize`), but only at a
 *     path-segment boundary.
 *   - An entry ending in `/` (`/auth/mfa/`) is an explicit prefix and matches by
 *     containment — the trailing slash is what stops it swallowing a future
 *     `/auth/mfa-settings`.
 *
 * The segment boundary is the point. A plain `url.includes('/auth/me')` also
 * matches `/auth/members` and `/auth/login` also matches `/auth/login-history`,
 * so any future route sharing a prefix with one of these would silently lose the
 * refresh-and-retry — the opposite of #637, and just as invisible. No such route
 * exists today (verified across every auth URL in the frontend), so this is a
 * guard on the next one rather than a fix for a live defect.
 *
 * The query string is stripped first: `?` is not a segment boundary, and
 * `/auth/me?x=1` must still match.
 */
function isSkippedPath(url) {
    const path = String(url || '').split(/[?#]/)[0];
    return SKIP_PATHS.some((entry) => {
        if (entry.endsWith('/')) return path.includes(entry);
        const i = path.indexOf(entry);
        if (i === -1) return false;
        const next = path[i + entry.length];
        return next === undefined || next === '/';
    });
}

let refreshPromise = null;

/**
 * Store a freshly issued token pair and tell AuthContext about it. Shared by the
 * silent refresh and the in-session MFA step-up (#854, `lib/stepUp.js`), so a
 * new pair always lands the same way.
 */
export function storeSessionTokens(accessToken, refreshToken) {
    localStorage.setItem('crm_token', accessToken);
    if (refreshToken) localStorage.setItem('crm_refresh_token', refreshToken);
    window.dispatchEvent(new CustomEvent(TOKEN_REFRESHED_EVENT, { detail: { token: accessToken } }));
}

/**
 * Point a request config at a new access token before it is sent again.
 * Callers pass `{ headers: getAuthHeader() }` per request, so a replay must
 * overwrite the header on the config itself. Shared by the 401 refresh retry
 * and the 403 step-up retry (#854).
 */
export function setBearer(config, token) {
    if (typeof config.headers?.set === 'function') {
        config.headers.set('Authorization', `Bearer ${token}`);
    } else {
        config.headers = { ...(config.headers || {}), Authorization: `Bearer ${token}` };
    }
}

function clearStoredAuth() {
    localStorage.removeItem('crm_token');
    localStorage.removeItem('crm_refresh_token');
    localStorage.removeItem('crm_impersonating');
}

/**
 * Exchange the stored refresh token for a new access token.
 * Single-flight: concurrent callers share one in-flight refresh.
 * Returns the new access token, or null if the session is truly over
 * (in which case stored auth is cleared and SESSION_EXPIRED_EVENT fires).
 */
export function refreshAccessToken() {
    if (refreshPromise) return refreshPromise;

    refreshPromise = (async () => {
        const refreshToken = localStorage.getItem('crm_refresh_token');
        if (!refreshToken) {
            expireSession();
            return null;
        }
        try {
            const res = await getRawClient().post(`${API}/auth/refresh`, { refresh_token: refreshToken }, { timeout: 15000 });
            const { access_token: newToken, refresh_token: newRefresh } = res.data;
            storeSessionTokens(newToken, newRefresh);
            return newToken;
        } catch (err) {
            // Only end the session on a definitive rejection. Network blips or
            // 5xx during a deploy keep the tokens so a later attempt can succeed.
            const status = err?.response?.status;
            if (status === 401 || status === 403) {
                expireSession();
                return null;
            }
            throw err;
        } finally {
            refreshPromise = null;
        }
    })();

    return refreshPromise;
}

/**
 * Resolves once any refresh in flight has finished; never rejects. The MFA
 * step-up (#854) retires the refresh token it is sent, so it must not race a
 * refresh that is still using the same one.
 */
export function whenRefreshSettled() {
    return refreshPromise ? refreshPromise.then(() => undefined, () => undefined) : Promise.resolve();
}

function expireSession() {
    const wasLoggedIn = !!localStorage.getItem('crm_token');
    clearStoredAuth();
    if (wasLoggedIn) {
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    }
}

/**
 * Decode the exp claim (seconds since epoch) from a JWT without verifying it.
 * Returns null for anything that is not a JWT (e.g. a PAT) or malformed input.
 */
export function getTokenExpiry(token) {
    try {
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
    } catch {
        return null;
    }
}

let interceptorInstalled = false;

/**
 * Global axios response interceptor: on a 401 from an authenticated request,
 * silently refresh the access token and retry the original request once.
 * If the refresh fails the session-expired event fires and the UI shows the
 * "signed out" dialog instead of scattering random per-page error messages.
 */
export function installAuthInterceptor() {
    if (interceptorInstalled) return;
    interceptorInstalled = true;

    axios.interceptors.response.use(
        (response) => response,
        async (error) => {
            const { config, response } = error;
            if (!config || response?.status !== 401) return Promise.reject(error);
            if (config._authRetry) return Promise.reject(error);
            if (isSkippedPath(config.url)) return Promise.reject(error);
            // Nothing to refresh with (public page / already logged out)
            if (!localStorage.getItem('crm_refresh_token')) return Promise.reject(error);

            let newToken = null;
            try {
                newToken = await refreshAccessToken();
            } catch {
                // Transient refresh failure — surface the original error
                return Promise.reject(error);
            }
            if (!newToken) {
                error.sessionExpired = true;
                return Promise.reject(error);
            }

            config._authRetry = true;
            setBearer(config, newToken);
            return axios(config);
        }
    );
}
